import React, { useMemo, useRef, useState } from 'react';
import _get from 'lodash/get';
import styles from './index.module.scss';

import { AdditionalFeatureSection, UiKit } from 'components';
import Header from '../Header';
import FeatureModal from './FeatureModal';
import { useFetch, useMutation, useRetailer } from 'utils/hooks';
import { MODAL_TYPES } from './stubs';

export default function AdditionalOptions() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [modalType, setModalType] = useState(null);
  const onReloadRef = useRef(null);

  const { isSSRStore } = useRetailer();

  const { data: termsData, isLoading, fetchData } = useFetch(
    'configure.carriers.terms',
    {
      loadOnMount: isSSRStore,
      onSuccess: data => setAgreeTerms(_get(data, 'are_terms_accepted', false))
    }
  );

  const isUpsSupported = _get(termsData, 'is_supported', true);
  const areTermsAccepted = _get(termsData, 'are_terms_accepted', false);
  const modalTitle = isUpsSupported
    ? 'Required Terms to Ship with UPS'
    : 'Missing Wallet Activation';

  // Mutation to accept terms
  const { mutate: onAcceptTerms, isProcessing: isAcceptingTerms } = useMutation(
    'configure.carriers.terms',
    'PUT',
    {
      payload: () => ({ feature_type: modalType, is_enabled: true }),
      errorMessage: 'Something went wrong while accepting the terms.',
      onValidate: () => isUpsSupported,
      onSuccess: () => {
        fetchData();
        setIsModalOpen(false);
        if (onReloadRef.current) {
          onReloadRef.current();
        }
      }
    }
  );

  const customModalToggle = useMemo(() => {
    if (!isSSRStore || areTermsAccepted) return null;

    const setModal = (type, onReload) => {
      setIsModalOpen(true);
      setModalType(type);
      onReloadRef.current = onReload;
    };

    return {
      is_no_box_label_enabled: {
        onEnable: (_, onReload) => setModal(MODAL_TYPES.UPS_NBNL, onReload)
      },
      is_ups_no_label_supported: {
        onEnable: (_, onReload) => setModal(MODAL_TYPES.UPS_PAPERLESS, onReload)
      }
    };
  }, [isSSRStore, areTermsAccepted]);

  return (
    <UiKit.Grid templateColumns="1fr" gap="18">
      <Header
        title="Additional Options"
        description="Choose the option of return label you want to offer your customers. Some options may depend on carrier support."
      />
      <AdditionalFeatureSection
        loaderCount={isSSRStore ? 2 : 1}
        isConfirmationRequired={false}
        includeFeature={[
          'is_qr_supported',
          'is_ups_no_label_supported',
          'is_no_box_label_enabled'
        ]}
        containerClassName={styles.features}
        customModalToggle={customModalToggle}
        externalLoading={isLoading}
      />
      <FeatureModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        isProcessing={isAcceptingTerms}
        isUpsSupported={isUpsSupported}
        title={modalTitle}
        onAcceptTerms={() => onAcceptTerms()}
        agreeTerms={agreeTerms}
        setAgreeTerms={setAgreeTerms}
      />
    </UiKit.Grid>
  );
}
